import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ImageIcon, Upload, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { uploadWithThumbnail } from '@/lib/thumbnailGenerator';
import type { StakeholderAttachment } from '@/pages/admin/StakeholderManagement';
import type { Json } from '@/integrations/supabase/types';

interface StakeholderImageUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stakeholderId: string;
  currentAttachments?: StakeholderAttachment[];
  onSuccess: (newAttachments: StakeholderAttachment[]) => void;
}

export const StakeholderImageUploadDialog = ({
  open,
  onOpenChange,
  stakeholderId,
  currentAttachments = [],
  onSuccess
}: StakeholderImageUploadDialogProps) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Current image from attachments
  const currentImage = currentAttachments.find(att => att.type?.startsWith('image/'));

  // Paste handler
  useEffect(() => {
    if (!open) return;

    const handlePaste = async (e: ClipboardEvent) => {
      e.preventDefault();
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            await handleFileUpload(file);
            return;
          }
        }
      }
      toast.error('Không tìm thấy hình ảnh trong clipboard');
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [open]);

  // Upload logic
  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Vui lòng chọn file hình ảnh');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    // Move interval ref outside try for proper cleanup in finally
    let progressInterval: ReturnType<typeof setInterval> | null = null;
    
    try {
      progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const result = await uploadWithThumbnail(
        file,
        'stakeholder-attachments',
        `${stakeholderId}/`,
        supabase
      );

      setUploadProgress(100);

      // Build new attachment object
      const newAttachment: StakeholderAttachment = {
        path: result.fullUrl.split('/').slice(-2).join('/'), // Extract relative path
        name: file.name,
        type: file.type,
        size: file.size,
        uploadedAt: new Date().toISOString()
      };

      // Replace existing image or add new one
      const otherAttachments = currentAttachments.filter(att => !att.type?.startsWith('image/'));
      const newAttachments = [...otherAttachments, newAttachment];

      // Update database
      const { error } = await supabase
        .from('stakeholders')
        .update({ attachments: newAttachments as unknown as Json })
        .eq('id', stakeholderId);

      if (error) throw error;

      onSuccess(newAttachments);
      onOpenChange(false);
      toast.success('Đã upload hình ảnh thành công');
    } catch (error: any) {
      console.error('Image upload failed', error);
      toast.error('Lỗi khi upload hình ảnh', { description: error.message });
    } finally {
      // Always cleanup interval regardless of success/error
      if (progressInterval) clearInterval(progressInterval);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Remove current image
  const handleRemoveImage = async () => {
    try {
      const newAttachments = currentAttachments.filter(att => !att.type?.startsWith('image/'));
      
      const { error } = await supabase
        .from('stakeholders')
        .update({ attachments: newAttachments as unknown as Json })
        .eq('id', stakeholderId);

      if (error) throw error;

      onSuccess(newAttachments);
      toast.success('Đã xóa hình ảnh');
    } catch (error: any) {
      toast.error('Lỗi khi xóa hình ảnh', { description: error.message });
    }
  };

  // Drag & drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) {
      await handleFileUpload(file);
    } else {
      toast.error('Vui lòng thả file hình ảnh');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cập nhật hình ảnh</DialogTitle>
          <DialogDescription>
            Kéo thả, paste (Ctrl+V), hoặc chọn file để upload
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current image preview */}
          {currentImage && (
            <div className="relative">
              <div className="text-sm text-muted-foreground mb-2">Hình ảnh hiện tại:</div>
              <div className="relative w-24 h-24 border rounded-lg overflow-hidden bg-muted">
                <img 
                  src={`${supabase.storage.from('stakeholder-attachments').getPublicUrl(currentImage.path).data.publicUrl}`}
                  alt="Current"
                  className="w-full h-full object-cover"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-1 right-1 h-6 w-6"
                  onClick={handleRemoveImage}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}

          {/* Upload area */}
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer",
              dragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50",
              uploading && "pointer-events-none opacity-50"
            )}
            onDragEnter={handleDragEnter}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
              disabled={uploading}
            />
            
            <div className="flex flex-col items-center gap-2">
              {uploading ? (
                <>
                  <Upload className="h-8 w-8 text-primary animate-pulse" />
                  <Progress value={uploadProgress} className="w-full max-w-[200px]" />
                  <span className="text-sm text-muted-foreground">Đang upload...</span>
                </>
              ) : (
                <>
                  <ImageIcon className="h-8 w-8 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Kéo thả hoặc click để chọn ảnh
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Hoặc Ctrl+V để paste
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Đóng
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
